import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, FileSpreadsheet, AlertTriangle, Printer, Mail } from 'lucide-react';
import Button from '../../ui/Button';
import nurseryService from '../../../services/nursery';
import { errorMessage } from '../../../utils/apiResponse';
import { downloadBlob } from '../../../utils/downloadBlob';
import { openDocument } from '../../../utils/openDocument';
import { formatDateTimeInTz } from '../../../utils/timezone';
import { cn } from '../../../utils/cn';
import type { SupplierManifest, SupplierManifestHeader, SupplierManifestOrder, SupplierManifestState } from '../../../types/nursery';

/**
 * The supplier manifest Bunnings' greenlife cross dock (Plant Access) wants
 * with the truck: one row per purchase order, with what it went on. The file
 * is Bunnings' own workbook with these filled in, so its store lookup,
 * checks and totals still work when they open it.
 *
 * Trees come from the picks once picking has started, else from the order,
 * and the row says which. The docket number is D and the order's invoice
 * number; an order not invoiced yet goes on with its order number, and says so. Trays, stillages and racks are known to nobody but
 * whoever loaded the truck, so they are typed here and kept on the load.
 *
 * Two stores' trees often share a stillage. The PO riding on another's picks
 * it under "Stillage"; it then has no stillage count, and the sheet names
 * whose stillage it is in its "Shared stillage with" column.
 *
 * The usual recipients are the organisation's, kept from one manifest to the
 * next; changing them here changes them for every load.
 *
 * A load can carry stores in several states, and each state's cross dock
 * gets its own manifest (Rob, 2026-10-14): the purchase orders are listed per
 * state, each with its own stacks, pallets and PHAC numbers, a stillage is
 * shared only within a state, and Download gives one workbook per state. The
 * server decides which state a store is in.
 */

export interface SupplierManifestDialogProps {
  loadId: number;
  timezone: string;
  onClose: () => void;
}

type Packing = { trays: string; stillages: string; seedling_racks: string; shares_with: string };
const EMPTY: Packing = { trays: '', stillages: '', seedling_racks: '', shares_with: '' };
type HeaderText = Record<Exclude<keyof SupplierManifestHeader, StateField>, string>;
// Typed once per state's manifest.
type StateField = 'total_stacks' | 'total_pallets' | 'phac_numbers';
type StateText = Record<StateField, string>;
// A state's key here and in ?state=: its code, or 'none', as the server keys it.
const stateKey = (state: string | null) => state ?? 'none';
type Action = 'save' | 'download' | 'print' | 'email';

// Labels: header.<key> (and header.<key>Hint) in the SupplierManifestDialog locale file.
const HEADER_FIELDS: { key: keyof HeaderText; hint?: boolean; inputMode?: 'email' | 'tel' }[] = [
  { key: 'supplier_code', hint: true },
  { key: 'batch_number' },
  { key: 'contact_name' },
  { key: 'contact_phone', inputMode: 'tel' },
  { key: 'contact_email', inputMode: 'email' },
];

// Labels: packing.<key> in the locale file.
const PACKING: { key: Exclude<keyof Packing, 'shares_with'> }[] = [
  { key: 'trays' },
  { key: 'stillages' },
  { key: 'seedling_racks' },
];

const asText = (n: number | null | undefined) => (n == null ? '' : String(n));
const asCount = (s: string): number | null => {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isInteger(n) && n >= 0 ? n : NaN;
};
const storeOf = (o: SupplierManifestOrder) => o.store_name || o.customer_name || o.order_number;
const parseEmails = (text: string) => [...new Set(text.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean))];
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The manifest's states. A server from before the split sends none: one manifest with everything. */
function statesOf(m: SupplierManifest): SupplierManifestState[] {
  return m.states ?? [{
    state: null,
    label: '',
    order_ids: m.orders.map((o) => o.order_id),
    total_stacks: m.header.total_stacks ?? null,
    total_pallets: m.header.total_pallets ?? null,
    phac_numbers: m.header.phac_numbers ?? null,
  }];
}

const inputClass ='w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500';

export function SupplierManifestDialog({ loadId, timezone, onClose }: SupplierManifestDialogProps) {
  const { t } = useTranslation('nursery', { keyPrefix: 'SupplierManifestDialog' });
  const [manifest, setManifest] = useState<SupplierManifest | null>(null);
  const [header, setHeader] = useState<HeaderText | null>(null);
  const [stateText, setStateText] = useState<Record<string, StateText>>({});
  const [packing, setPacking] = useState<Record<number, Packing>>({});
  const [recipients, setRecipients] = useState('');
  const [busy, setBusy] = useState<Action | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const take = (m: SupplierManifest) => {
    setManifest(m);
    setHeader({
      supplier_code: m.header.supplier_code ?? '',
      batch_number: m.header.batch_number ?? '',
      contact_name: m.header.contact_name ?? '',
      contact_phone: m.header.contact_phone ?? '',
      contact_email: m.header.contact_email ?? '',
    });
    setStateText(Object.fromEntries(statesOf(m).map((s) => [stateKey(s.state), {
      total_stacks: asText(s.total_stacks),
      total_pallets: asText(s.total_pallets),
      phac_numbers: s.phac_numbers ?? '',
    }])));
    setPacking(Object.fromEntries(m.orders.map((o) => [o.order_id, {
      trays: asText(o.trays), stillages: asText(o.stillages), seedling_racks: asText(o.seedling_racks),
      shares_with: o.shares_with_order_id ? String(o.shares_with_order_id) : '',
    }])));
    setRecipients((m.recipients ?? []).join(', '));
  };

  useEffect(() => {
    nurseryService.getLoadManifest(loadId)
      .then((res) => (res.success ? take(res.data) : setError(errorMessage(res, t('openFailed')))))
      .catch((e) => setError(errorMessage(e, t('openFailed'))));
  }, [loadId, t]);

  const orders = manifest?.orders ?? [];
  const states = manifest ? statesOf(manifest) : [];
  const several = states.length > 1;
  const stateOf = (o: SupplierManifestOrder) => o.state ?? null;
  const counts = orders.map((o) => {
    const p = packing[o.order_id] ?? EMPTY;
    const sharesWith = p.shares_with ? Number(p.shares_with) : null;
    return {
      order_id: o.order_id,
      trays: asCount(p.trays),
      // On another's stillage: counted on that row, not this one.
      stillages: sharesWith ? null : asCount(p.stillages),
      seedling_racks: asCount(p.seedling_racks),
      shares_with_order_id: sharesWith,
    };
  });
  // Only an order with its own stillage can carry another's trees, and only
  // one going to the same state: each state has its own manifest.
  const hosts = (o: SupplierManifestOrder) => orders.filter((h) => h.order_id !== o.order_id
    && !packing[h.order_id]?.shares_with && stateOf(h) === stateOf(o));
  const riders = (o: SupplierManifestOrder) => orders.filter((r) => packing[r.order_id]?.shares_with === String(o.order_id));
  const totals = states.map((s) => {
    const text = stateText[stateKey(s.state)];
    return {
      state: s.state,
      total_stacks: asCount(text?.total_stacks ?? ''),
      total_pallets: asCount(text?.total_pallets ?? ''),
      phac_numbers: text?.phac_numbers.trim() || null,
    };
  });
  const invalid = counts.some((c) => [c.trays, c.stillages, c.seedling_racks].some((n) => Number.isNaN(n)))
    || totals.some((s) => Number.isNaN(s.total_stacks) || Number.isNaN(s.total_pallets));
  const emails = parseEmails(recipients);
  const badEmails = emails.filter((e) => !EMAIL.test(e));
  const noPo = orders.filter((o) => !o.customer_po);
  const noInvoice = orders.filter((o) => !o.docket_number);
  const notPicked = orders.some((o) => o.items_from === 'ordered');
  const sum = (ids: Set<number>, key: 'items' | 'trays' | 'stillages' | 'seedling_racks') => (key === 'items'
    ? orders.filter((o) => ids.has(o.order_id)).reduce((n, o) => n + o.items, 0)
    : counts.filter((c) => ids.has(c.order_id)).reduce((n, c) => n + (Number(c[key]) || 0), 0));
  const blocked = busy !== null || !manifest || invalid || badEmails.length > 0;

  const save = async (): Promise<boolean> => {
    if (!header) return false;
    // A server from before the split reads these from the header.
    const only = totals.length === 1 ? totals[0] : null;
    const res = await nurseryService.saveLoadManifest(loadId, {
      header: {
        supplier_code: header.supplier_code.trim() || null,
        batch_number: header.batch_number.trim() || null,
        phac_numbers: only?.phac_numbers ?? null,
        contact_name: header.contact_name.trim() || null,
        contact_phone: header.contact_phone.trim() || null,
        contact_email: header.contact_email.trim() || null,
        total_stacks: only?.total_stacks ?? null,
        total_pallets: only?.total_pallets ?? null,
      },
      orders: counts,
      ...(manifest?.states ? { states: totals } : {}),
      recipients: emails,
    });
    if (!res.success) { setError(errorMessage(res, t('saveFailed'))); return false; }
    take(res.data);
    return true;
  };

  const name = `supplier-manifest-${manifest?.load.load_number ?? loadId}`;
  const run = async (what: Action) => {
    setBusy(what);
    setError(null);
    setDone(null);
    try {
      // Whatever was typed goes into the file, so save first, always.
      if (!(await save())) return;
      if (what === 'download') {
        // One workbook per state, each named for its state.
        if (several) {
          for (const s of states) {
            const key = stateKey(s.state);
            downloadBlob(await nurseryService.getLoadManifestFile(loadId, key), `${name}-${key}.xlsx`);
          }
        } else {
          downloadBlob(await nurseryService.getLoadManifestFile(loadId), `${name}.xlsx`);
        }
      } else if (what === 'print') {
        await openDocument(`/nursery/loads/${loadId}/manifest.pdf`, `${name}.pdf`);
      } else if (what === 'email') {
        const res = await nurseryService.emailLoadManifest(loadId, emails);
        if (!res.success) { setError(errorMessage(res, t('emailFailed'))); return; }
        take(res.data);
        setDone(t('emailedTo', { to: emails.join(', ') }));
      } else {
        setDone(t('saved'));
      }
    } catch (e) {
      setError(errorMessage(e, what === 'save' ? t('saveFailed') : what === 'email' ? t('emailFailed') : t('makeFailed')));
      // A failed send is logged on the load; show it in the list.
      if (what === 'email') {
        nurseryService.getLoadManifest(loadId).then((res) => { if (res.success) setManifest(res.data); }).catch(() => undefined);
      }
    } finally {
      setBusy(null);
    }
  };

  const setHead = (key: keyof HeaderText, value: string) => setHeader((h) => (h ? { ...h, [key]: value } : h));
  const setStateField = (state: string | null, key: StateField, value: string) =>
    setStateText((all) => {
      const k = stateKey(state);
      const was = all[k] ?? { total_stacks: '', total_pallets: '', phac_numbers: '' };
      return { ...all, [k]: { ...was, [key]: value } };
    });
  const stateName = (s: SupplierManifestState) => (s.state ?? t('stateUnknown'));
  const setPack = (orderId: number, key: keyof Packing, value: string) =>
    setPacking((p) => ({ ...p, [orderId]: { ...(p[orderId] ?? EMPTY), [key]: value } }));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="manifest-title">
      <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-xl shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 px-5 pt-5">
          <div className="min-w-0">
            <h2 id="manifest-title" className="text-lg font-semibold text-neutral-900">{t('title')}</h2>
            <p className="text-sm text-neutral-500">
              {t('intro')}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label={t('close')} className="p-1 rounded hover:bg-neutral-100 flex-shrink-0">
            <X className="h-5 w-5 text-neutral-500" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>}
          {done && <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm text-green-800 break-words">{done}</div>}

          {!manifest || !header ? (
            !error && <div className="h-40 bg-neutral-100 rounded-lg animate-pulse" />
          ) : (
            <>
              <section aria-labelledby="manifest-supplier" className="space-y-3">
                <h3 id="manifest-supplier" className="text-sm font-semibold text-neutral-700">{t('supplier')}</h3>
                <p className="text-sm text-neutral-700 break-words">
                  <span className="font-medium">{manifest.supplier.name}</span>
                  {manifest.supplier.address ? <span className="text-neutral-500"> · {manifest.supplier.address}</span> : null}
                </p>
                {!manifest.saved && (
                  <p className="text-xs text-neutral-500">{t('filledIn')}</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {HEADER_FIELDS.map((f) => (
                    <label key={f.key} className="block text-sm">
                      <span className="text-neutral-700">{t(`header.${f.key}`)}</span>
                      <input type="text" inputMode={f.inputMode} className={`${inputClass} mt-1`}
                        value={header[f.key]} onChange={(e) => setHead(f.key, e.target.value)} />
                      {f.hint && <span className="text-xs text-neutral-500">{t(`header.${f.key}Hint`)}</span>}
                    </label>
                  ))}
                </div>
              </section>

              <section aria-labelledby="manifest-orders" className="space-y-2">
                <h3 id="manifest-orders" className="text-sm font-semibold text-neutral-700">
                  {t('purchaseOrders')} <span className="font-normal text-neutral-500">({orders.length})</span>
                </h3>
                <p className="text-xs text-neutral-500">
                  {t('sharedHelp')}
                  {notPicked && <>{' '}{t('notPickedHelp')}</>}
                </p>
                {noPo.length > 0 && (
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-900">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span className="min-w-0 break-words">
                      {t('noPo', { count: noPo.length, orders: noPo.map((o) => o.order_number).join(', ') })}
                    </span>
                  </div>
                )}
                {noInvoice.length > 0 && (
                  <p className="text-xs text-neutral-500 break-words">
                    {noInvoice.length === orders.length ? t('noInvoiceNone') : t('noInvoiceSome', { count: noInvoice.length })}
                  </p>
                )}
                {several && (
                  <p className="text-xs text-neutral-500 break-words">{t('byState', { states: states.map(stateName).join(', ') })}</p>
                )}
              </section>

              {states.map((s) => {
                const ids = new Set(s.order_ids);
                const mine = orders.filter((o) => ids.has(o.order_id));
                const text = stateText[stateKey(s.state)] ?? { total_stacks: '', total_pallets: '', phac_numbers: '' };
                const forState = (label: string) => (several ? t('fieldForState', { label, state: stateName(s) }) : undefined);
                return (
                  <section key={stateKey(s.state)} aria-label={several ? t('manifestFor', { state: stateName(s) }) : undefined}
                    className={cn('space-y-2', several && 'border-t border-neutral-200 pt-3')}>
                    {several && (
                      <h4 className="text-sm font-semibold text-neutral-900">
                        {t('manifestFor', { state: stateName(s) })} <span className="font-normal text-neutral-500">({mine.length})</span>
                      </h4>
                    )}
                    {s.state === null && manifest.states && (
                      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-900">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <span className="min-w-0 break-words">{t('stateUnknownHelp')}</span>
                      </div>
                    )}
                    <ul className="space-y-2">
                      {mine.map((o) => {
                        const p = packing[o.order_id] ?? EMPTY;
                        const carrying = riders(o);
                        return (
                          <li key={o.order_id} className="border border-neutral-200 rounded-lg p-3">
                            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
                              <span className="font-medium text-neutral-900 break-words min-w-0">{o.customer_name ?? t('noCustomer')}</span>
                              {o.customer_code && <span className="text-neutral-500">#{o.customer_code}</span>}
                              <span className="ml-auto text-neutral-700 whitespace-nowrap">
                                {t('trees', { count: o.items })}
                                <span className="text-neutral-500"> {o.items_from === 'picked' ? t('picked') : t('ordered')}</span>
                              </span>
                            </div>
                            <p className="text-xs text-neutral-500 break-words">
                              {t('poDocket', { po: o.customer_po ?? '—', docket: o.docket_number ?? t('notInvoicedYet', { order: o.order_number }) })}
                              {o.items_from === 'picked' && o.picked_trees < o.ordered_trees ? ` · ${t('shortOfOrder', { n: o.ordered_trees - o.picked_trees })}` : ''}
                            </p>
                            <label className="block text-xs text-neutral-600 mt-2">
                              {t('stillage')}
                              <select className={`${inputClass} mt-0.5`} aria-label={t('stillageFor', { order: o.order_number })}
                                value={p.shares_with} disabled={carrying.length > 0}
                                onChange={(e) => setPack(o.order_id, 'shares_with', e.target.value)}>
                                <option value="">{t('itsOwn')}</option>
                                {hosts(o).map((h) => (
                                  <option key={h.order_id} value={String(h.order_id)}>
                                    {h.customer_po ? t('sharedWithPo', { store: storeOf(h), po: h.customer_po }) : t('sharedWith', { store: storeOf(h) })}
                                  </option>
                                ))}
                              </select>
                            </label>
                            {carrying.length > 0 && (
                              <p className="text-xs text-neutral-500 mt-1 break-words">
                                {t('alsoCarrying', { stores: carrying.map(storeOf).join(', ') })}
                              </p>
                            )}
                            <div className="grid grid-cols-3 gap-2 mt-2">
                              {PACKING.map((f) => (f.key === 'stillages' && p.shares_with ? (
                                <p key={f.key} className="text-xs text-neutral-500 self-end pb-2">{t('countedOnShared')}</p>
                              ) : (
                                <label key={f.key} className="block text-xs text-neutral-600">
                                  {t(`packing.${f.key}`)}
                                  <input type="text" inputMode="numeric" className={`${inputClass} mt-0.5 text-right`}
                                    aria-label={t('packingFor', { label: t(`packing.${f.key}`), order: o.order_number })}
                                    value={p[f.key]} onChange={(e) => setPack(o.order_id, f.key, e.target.value)} />
                                </label>
                              )))}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    <p className="text-sm text-neutral-700">
                      {t('total', { trees: sum(ids, 'items'), trays: sum(ids, 'trays'), stillages: sum(ids, 'stillages'), racks: sum(ids, 'seedling_racks') })}
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="block text-sm">
                        <span className="text-neutral-700">{t('totalStacks')}</span>
                        <input type="text" inputMode="numeric" className={`${inputClass} mt-1`} aria-label={forState(t('totalStacks'))}
                          value={text.total_stacks} onChange={(e) => setStateField(s.state, 'total_stacks', e.target.value)} />
                      </label>
                      <label className="block text-sm">
                        <span className="text-neutral-700">{t('totalPallets')}</span>
                        <input type="text" inputMode="numeric" className={`${inputClass} mt-1`} aria-label={forState(t('totalPallets'))}
                          value={text.total_pallets} onChange={(e) => setStateField(s.state, 'total_pallets', e.target.value)} />
                      </label>
                      <label className="block text-sm col-span-2">
                        <span className="text-neutral-700">{t('phac')}</span>
                        <input type="text" className={`${inputClass} mt-1`} aria-label={forState(t('phac'))}
                          value={text.phac_numbers} onChange={(e) => setStateField(s.state, 'phac_numbers', e.target.value)} />
                      </label>
                    </div>
                  </section>
                );
              })}
              {invalid && <p className="text-sm text-red-700">{t('wholeNumbers')}</p>}

              <section aria-labelledby="manifest-email" className="space-y-2">
                <h3 id="manifest-email" className="text-sm font-semibold text-neutral-700">{t('emailTo')}</h3>
                <textarea rows={2} className={inputClass} aria-labelledby="manifest-email"
                  placeholder={t('emailPlaceholder')}
                  value={recipients} onChange={(e) => setRecipients(e.target.value)} />
                <p className="text-xs text-neutral-500">
                  {t('usualList')}
                </p>
                {badEmails.length > 0 && (
                  <p className="text-sm text-red-700 break-words">{t('notEmail', { list: badEmails.join(', ') })}</p>
                )}
                {(manifest.emails ?? []).length > 0 && (
                  <ul className="text-xs text-neutral-600 space-y-0.5">
                    {(manifest.emails ?? []).map((e) => (
                      <li key={e.sent_at} className={cn('break-words', !e.sent_ok && 'text-red-700')}>
                        {e.sent_ok
                          ? t('emailedLog', { date: formatDateTimeInTz(e.sent_at, timezone), to: e.recipients.join(', ') })
                          : t('notSentLog', { date: formatDateTimeInTz(e.sent_at, timezone), to: e.recipients.join(', ') })}
                        {e.sent_by ? ` ${t('by', { name: e.sent_by })}` : ''}{!e.sent_ok && e.error ? `: ${e.error}` : ''}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 px-5 pb-5">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy !== null}>{t('close')}</Button>
          <Button variant="outline" size="sm" onClick={() => run('save')} disabled={blocked}>
            {busy === 'save' ? t('saving') : t('save')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => run('print')} disabled={blocked || orders.length === 0}>
            <Printer className="h-4 w-4" /> {busy === 'print' ? t('making') : t('print')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => run('download')} disabled={blocked || orders.length === 0}>
            <FileSpreadsheet className="h-4 w-4" /> {busy === 'download' ? t('making') : t('download')}
          </Button>
          <Button size="sm" onClick={() => run('email')} disabled={blocked || orders.length === 0 || emails.length === 0}>
            <Mail className="h-4 w-4" /> {busy === 'email' ? t('sending') : emails.length ? t('emailPeople', { count: emails.length }) : t('email')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SupplierManifestDialog;
